import { EditorView } from '@codemirror/view'
import type { Extension } from '@codemirror/state'
import RecordeApi from '~~/package/apis/recorde/RecordeApi'
import type PreSignedUrlParamsRequest from '~~/package/apis/recorde/request/PreSignedUrlParamsRequest'

interface KeystrokeEntry { t: number; op: 'ins' | 'del'; n: number; bs: boolean }

const FLUSH_INTERVAL_MS = 60_000

export const useKeystrokeTelemetryService = () => {
  const recordeApi = new RecordeApi()
  const events: KeystrokeEntry[] = []
  let startedAtMs = 0
  let uploadParams: PreSignedUrlParamsRequest | null = null
  let flushTimer: ReturnType<typeof setInterval> | null = null

  // 문자 내용은 절대 기록하지 않는다 — 개수/타이밍/백스페이스 여부만.
  const _recordChange = ({ inserted, deleted }: { inserted: number; deleted: number }) => {
    const t = Date.now() - startedAtMs
    if (deleted > 0) events.push({ t, op: 'del', n: deleted, bs: inserted === 0 })
    if (inserted > 0) events.push({ t, op: 'ins', n: inserted, bs: false })
  }

  const extension = (): Extension =>
    EditorView.updateListener.of((update) => {
      if (!update.docChanged) return
      update.changes.iterChanges((fromA, toA, _fromB, _toB, ins) => {
        _recordChange({ inserted: ins.length, deleted: toA - fromA })
      })
    })

  // 여러 flush 호출(주기적 flush, stop()의 마지막 flush)이 동시에 진행되면
  // 먼저 시작했지만 늦게 끝나는 PUT이 더 최신(더 큰) 문서를 덮어써 데이터 유실이
  // 발생할 수 있다. flushChain으로 직렬화하여 한 번에 하나의 PUT만 진행되고
  // 호출 순서대로 완료되도록 보장한다.
  let flushChain: Promise<void> = Promise.resolve()

  const doFlush = async () => {
    if (!uploadParams || events.length === 0) return
    try {
      const doc = { v: 1, startedAtMs, events }
      const url = await recordeApi.getPreSignedUrl({ ...uploadParams, content_type: 'application/json' })
      await recordeApi.upload(url, new Blob([JSON.stringify(doc)], { type: 'application/json' }), 'application/json')
    } catch (e) {
      // 텔레메트리 실패는 응시를 방해하지 않는다
      console.warn('keystroke telemetry flush failed', e)
    }
  }

  const flush = (): Promise<void> => {
    flushChain = flushChain.then(() => doFlush()).catch(() => {})
    return flushChain
  }

  const start = (params: PreSignedUrlParamsRequest) => {
    if (flushTimer) {
      // 이전 start()가 stop() 없이 다시 호출된 경우: 이전 인터벌을 정리하지 않으면
      // 타이머가 누수되고, events를 그대로 비우면 미전송 이벤트가 조용히 사라진다.
      console.warn('keystroke telemetry: start() called again without stop(); restarting capture and discarding unflushed events')
      clearInterval(flushTimer)
      flushTimer = null
    }
    uploadParams = params
    startedAtMs = Date.now()
    events.length = 0
    flushTimer = setInterval(() => { void flush() }, FLUSH_INTERVAL_MS)
  }

  const stop = async () => {
    if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
    // flush()는 flushChain에 연결되므로, 이미 진행 중이거나 대기 중인 이전 flush가
    // 모두 끝난 뒤에 마지막 flush가 실행되고 그 완료까지 여기서 기다린다.
    await flush()
  }

  return { extension, start, stop, flush, _recordChange }
}
